import { useStorage } from "@vueuse/core";

import { initialAbility } from "@/libs/acl/config";
import ability from "@/libs/acl/ability";
import ToastificationContent from "@/components/toastification/ToastificationContent.vue";
import { getHomeRouteForLoggedInUser } from "@/auth/utils";
import { useForm } from "vee-validate";
import { required, email as emailV } from "@validations";

import useUsuarioStore from "@/store/usuario";
import useRootStore from "@/store/root";

import useSistemaStore from "@/store/sistema";
import useCarritoStore from "@/store/carrito";
import useDescuentoStore from "@/store/descuento";

export default function useAuth() {
  const router = useRouter();
  const usuario_store = useUsuarioStore();
  const root_store = useRootStore();
  const mostrarEntrevistaGlobal = inject("mostrarEntrevistaGlobal", () => ({}));
  const mostrarWelcomeGuia = inject("mostrarWelcomeGuia", () => ({}));

  const { limpiarUsuario, updateLocale, cargarUser } = usuario_store;
  const { cerrarSesion: cerrar, setAuthMessage, setToken } = root_store;

  const i18n = inject("i18n");
  const { usuario, conectado, usuario_id } = storeToRefs(usuario_store);

  const back_auth_user = useStorage("back_auth_user", {});

  const is_user_anterior = computed(() =>
    back_auth_user.value.id ? true : false
  );

  const optionsAuth = ref({
    client_id:
      "551558497756-8pabgs2uaeb3rau5vhjeoi8uk6htj1qg.apps.googleusercontent.com", // required
    auto_select: false, // optional
    cancel_on_tap_outside: false, // optional
    context: "signin", // optional
    itp_support: true,
    state_cookie_domain: window.location.origin,
  });

  const formValidate = ref(null);

  const formulario = ref({
    email: "",
    password: "",
    remember: false,
  });

  const { defineField, handleSubmit, errors, setErrors } = useForm({
    initialValues: formulario.value,
    validationSchema: {
      email: [required, emailV],
    },
  });

  const is_loggin = computed(() => (usuario.value.id ? true : false));

  const cerrarSesion = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("habilidades");
    localStorage.removeItem("userData");
    localStorage.removeItem("usuarioId");
    localStorage.removeItem("disk_name");
    localStorage.removeItem("chat_id");
    localStorage.removeItem("redirect_after_login");
    limpiarUsuario();
    ability.update(initialAbility);
  };

  const logout = () => {
    const descuento_store = useDescuentoStore();
    cerrar()
      .then(({ data }) => {
        cerrarSesion();
        localStorage.removeItem("back_auth_user");
        localStorage.removeItem("clienteSeleccionado");
        localStorage.setItem(
          "back_redirect_after_login",
          router.currentRoute.value.fullPath
        );
        descuento_store.clear();
        router.push({ name: "welcome" });
      })
      .catch(({ response }) => {
        if (response.status === 419) {
          router.push({ name: "welcome" });
        }
      });
  };

  const cerrarSesionAutomatica = async () => {
    try {
      const { recordado } = await usuario_store.verificarRememberMe(
        usuario_id.value
      );
      return { recordado };
    } catch (error) {
      throw error;
    }
  };

  const login = () => {
    return new Promise((resolv, reject) => {
      axios
        .post("/api/auth/login", formulario.value)
        .then(({ data }) => {
          postLogin(data);
          resolv(data.result);
        })
        .catch((error) => {
          const { response } = error;
          if (response && response.status === 422) {
            setErrors(response.data.errors);
            toast.error(
              error.response.data.message ||
                "Error de validación, por favor verifique los datos ingresados."
            );
          }
          reject(error);
        });
    });
  };

  const cargarSistema = () => {
    const sistema_store = useSistemaStore();
    sistema_store.fetch();
    montarEchoApp();
  };

  const cargarCarrito = (carrito_id) => {
    if (carrito_id) {
      const carrito_store = useCarritoStore();
      carrito_store.fetch(carrito_id);
    }
  };

  const postLogin = (data) => {
    const { usuario: u, accessToken } = data;

    axios.defaults.headers.common = {
      Authorization: `Bearer ${accessToken}`,
    };

    setAuthMessage(null);
    cargarUser(u);
    setToken(accessToken);

    localStorage.setItem("token", accessToken);
    localStorage.setItem("habilidades", JSON.stringify(u.habilidades));
    localStorage.setItem("userData", JSON.stringify(u));

    ability.update(JSON.parse(localStorage.getItem("habilidades")));

    updateLocale({
      usuario: data.usuario ? data.usuario.id : null,
      locale: i18n.locale,
    });

    cargarSistema();
    // mostrarWelcomeGuia();

    if (
      data.usuario &&
      data.usuario.entrevista_id &&
      !data.usuario.entrevista_inicio_terminada
    ) {
      // mostrarEntrevistaGlobal(data.usuario.entrevista_id);
    }
  };

  const authenticarGoogle = (
    {
      client_id,
      auto_select = false,
      cancel_on_tap_outside = false,
      context = "signin",
      ...otherOptions
    },
    callback = sendResponseAuthGoogle
  ) => {
    try {
      if (window.cookieStore.get("g_state")) {
        window.cookieStore.delete("g_state");
      }
    } catch (error) {
      console.log(error);
    }

    if (!client_id) {
      throw new Error("client_id is required");
    }

    if (typeof window !== "undefined" && window.document) {
      setTimeout(() => {
        const contextValue = ["signin", "signup", "use"].includes(context)
          ? context
          : "signin";
        // const googleScript = document.createElement("script");

        // googleScript.src = "https://accounts.google.com/gsi/client";
        // googleScript.async = true;
        // googleScript.defer = true;
        // document.head.appendChild(googleScript);

        window.google.accounts.id.initialize({
          client_id: client_id,
          callback: callback,
          auto_select: auto_select,
          cancel_on_tap_outside: cancel_on_tap_outside,
          context: contextValue,
          ...otherOptions,
        });

        window.google.accounts.id.prompt();
      }, 2000);
    }
  };

  const authGoogle = () => {
    const isLoggedIn = localStorage.getItem("token") ? true : false;

    if (!isLoggedIn) {
      authenticarGoogle(optionsAuth.value);
      // googleOneTap(optionsAuth.value, sendResponseAuthGoogle);
    }
  };

  const montarEchoApp = () => {
    if (usuario_id.value && typeof Echo !== "undefined") {
      Echo.private(`usuario.${usuario_id.value}`)
        .listen(".paginas.descontadas", ({ desc, nueva_cantidad }) => {
          usuario_store.actualizarPaginas(nueva_cantidad);
        })
        .listen(".paginas.agregadas", ({ nueva_cantidad }) => {
          console.log("Actualizando páginas agregadas", nueva_cantidad);
          usuario_store.actualizarPaginas(nueva_cantidad);
        })
        .listen(".permisos.actualizados", () => {
          usuario_store.refetchUsuario(usuario_id.value);
          preguntar(
            {
              title:
                "Tus permisos han sido actualizados. La aplicación se recargará para aplicar los nuevos cambios.",
            },
            () => {
              location.reload();
            }
          );
        });
    }
  };

  const sendResponseAuthGoogle = (response) => {
    axios
      .post("/api/auth/google", {
        ...response,
        clientId: optionsAuth.value.client_id,
      })
      .then(({ data }) => {
        postLogin(data);
        router
          .replace(getHomeRouteForLoggedInUser(usuario.value.rol.nombre))
          .then(() => {
            toast(
              {
                component: ToastificationContent,
                props: {
                  title: `Hola ${usuario.value.nombre}`,
                  icon: "fa-mug-hot",
                  variant: "success",
                  text: `Has iniciado sesión correctamente como ${usuario.value.rol.nombre}. ¡Ahora puedes empezar a explorar!`,
                },
              },
              {
                position: "bottom-right",
                timeout: 4000,
              }
            );
          });
      })
      .catch((e) => console.log(e));
  };

  const authUser = ({ id: user_id, nombre, apellido }, next) => {
    preguntar(
      {
        title: `¿Está seguro de iniciar sesión como ${nombre} ${
          apellido || ""
        }?`,
      },
      () => {
        axios
          .post("/api/usuarios/auth-other-user", { usuario_id: user_id })
          .then(({ data }) => {
            if (data.result) {
              localStorage.setItem(
                "back_auth_user",
                JSON.stringify(usuario.value)
              );

              cerrarSesion();

              postLogin(data);
              next(data.result);
            } else {
              toast.error("No se pudo autenticar");
            }
          })
          .catch((e) => {
            console.log(e);
            toast.error("No se pudo autenticar");
          });
      }
    );
  };

  const regresarAuthAnterior = (next) => {
    if (is_user_anterior.value) {
      axios
        .post("/api/usuarios/auth-other-user", {
          usuario_id: back_auth_user.value.id,
        })
        .then(({ data }) => {
          if (data.result) {
            cerrarSesion();
            localStorage.removeItem("back_auth_user");

            postLogin(data);

            next(data.result);
          } else {
            toast.error("No se pudo autenticar");
          }
        })
        .catch((e) => {
          console.log(e);
          toast.error("No se pudo autenticar");
        });
    }
  };

  const igual = (u_id) => {
    if (is_loggin.value && usuario.value.id === u_id) {
      return true;
    }

    return false;
  };

  watch(usuario_id, () => {
    montarEchoApp();
  });

  const iniciarConGoogle = () => {
    authGoogle();
  };

  watch(is_loggin, () => {
    if (is_loggin.value) {
      if (typeof window !== "undefined") {
        try {
          if (
            window.google &&
            window.google.accounts &&
            window.google.accounts.id &&
            typeof window.google.accounts.id.cancel === "function"
          ) {
            window.google.accounts.id.cancel(); // cierra One Tap
          }

          if (
            window.google &&
            window.google.accounts &&
            window.google.accounts.id &&
            typeof window.google.accounts.id.disableAutoSelect === "function"
          ) {
            window.google.accounts.id.disableAutoSelect(); // evita auto select
          }

          // intenta eliminar estado de g_state
          if (
            window.cookieStore &&
            typeof window.cookieStore.delete === "function"
          ) {
            window.cookieStore.delete("g_state").catch(() => {});
          } else {
            document.cookie =
              "g_state=; Max-Age=0; path=/; domain=" + location.hostname;
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
  });

  return {
    cerrarSesion,
    logout,
    login,
    authGoogle,
    authenticarGoogle,
    authUser,
    regresarAuthAnterior,
    montarEchoApp,
    cargarCarrito,
    igual,
    defineField,
    handleSubmit,
    errors,
    conectado,
    is_loggin,
    is_user_anterior,
    formulario,
    formValidate,
    optionsAuth,
    back_auth_user,
    cerrarSesionAutomatica,
    iniciarConGoogle,
  };
}
